/**
 * voiceCloningSlice.js
 *
 * Client state for the Book Creator Voice Cloning Pipeline page.
 *
 * Surface:
 *   - fetchPipeline(userId)            load all rows for a user (flattened)
 *   - seedClone(speechSampleId)        kick off the pipeline for an approved sample
 *   - reconcileClone(cloneUuid)        ask server to poll ElevenLabs, refresh row
 *   - retryClone(cloneUuid)            create a fresh attempt (seeded + queued)
 *   - smokeTestClone({ cloneUuid,
 *                      text })         TTS + persist preview URL
 *   - deleteClone(cloneUuid)           delete on ElevenLabs + remove local row
 *
 * Row shape exposed to the UI (flattened from the backend's
 *   { sampleId, ..., clone: {...} } envelope):
 *
 *     {
 *       uuid,              // clone.uuid OR sampleId (for "no-clone-yet" rows)
 *       sampleId,
 *       bookId, bookTitle,
 *       litGroupId, litGroupName,
 *       characterId, characterName,
 *       variationId, variationName,
 *       cloneStatus,       // 'none' if no clone has been seeded yet
 *       elevenLabsVoiceId,
 *       errorMessage,
 *       attemptNumber,
 *       smokeTestUrl,
 *       approvedAt,
 *       createdAt,
 *       updatedAt,
 *       hasClone           // boolean convenience flag
 *     }
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../constants/config';

const authHeaders = () => {
  const token = localStorage.getItem('authToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const handle = async (response, rejectWithValue) => {
  if (!response.ok) {
    let err;
    try {
      err = await response.json();
    } catch (_) {
      err = { message: response.statusText };
    }
    return rejectWithValue(err);
  }
  return response.json();
};

/**
 * Flatten the backend row envelope into the UI row shape.
 * `{ sampleId, ..., clone: { uuid, cloneStatus, elevenLabsVoiceId, ... } | null }`
 * becomes a single flat object, with `uuid = clone?.uuid || sampleId` so the
 * UI always has a stable key (including for samples that haven't been seeded
 * yet — those get cloneStatus='none').
 */
export const flattenPipelineRow = (row) => {
  if (!row) return null;
  const c = row.clone || null;
  // The server now returns a `normalize` block describing the state of the
  // pre-clone canonicalization step (VoiceSnippet.normalizeStatus). Keep it
  // as a nested object rather than spreading onto the row so the existing
  // clone-status fields aren't accidentally overwritten.
  const n = row.normalize || null;
  return {
    // ⛔ ROW IDENTITY IS THE SAMPLE, NOT THE CLONE.
    //
    // This was the clone uuid, which was unique while every sample had a clone of its own. A
    // POOLED clone is shared by every sample it serves, so nine variations arrived carrying one
    // id between them — React keyed them all the same and the list rendered a single row. The
    // server was returning all nine.
    //
    // The sample is what a row IS; the clone is what is currently attached to it. 
    // below still carries the clone for the actions that need it.
    uuid: row.sampleId,
    cloneUuid: c?.uuid || null,
    hasClone: !!c,
    sampleId: row.sampleId,
    approvedAt: row.approvedAt,
    bookId: row.bookId,
    bookTitle: row.bookTitle,
    litGroupId: row.litGroupId,
    litGroupName: row.litGroupName,
    characterId: row.characterId,
    characterName: row.characterName,
    variationId: row.variationId,
    variationName: row.variationName,
    cloneStatus: c?.cloneStatus || 'none',
    cloneKind: c?.cloneKind || null,
    elevenLabsVoiceId: c?.elevenLabsVoiceId || null,
    externalStatus: c?.externalStatus || null,
    errorMessage: c?.errorMessage || null,
    attemptNumber: c?.attemptNumber || 0,
    smokeTestUrl: c?.smokeTestUrl || null,
    // EVERY version of this variation's voice, not just the serving one.
    //
    // This flattener is an allow-list: a field the server adds is invisible to the UI until it is
    // named here.  was returned, signed and correct, and the comparison block that needs
    // it simply never saw it — which looked exactly like the feature not existing.
    clones: Array.isArray(row.clones) ? row.clones : [],
    cloneSourceCount: c?.sourceSampleCount ?? row.cloneSourceCount ?? null,
    cloneSourceSeconds: c?.sourceSeconds ?? row.cloneSourceSeconds ?? null,
    createdAt: c?.createdAt || null,
    updatedAt: c?.updatedAt || row.approvedAt || null,
    normalize: n ? {
      voiceSnippetUuid: n.voiceSnippetUuid || null,
      status: n.status || null,
      error: n.error || null,
      attempts: n.attempts || 0,
      trigger: n.trigger || null,
      probeDurationSec: n.probeDurationSec || null,
      probeCodec: n.probeCodec || null,
      probeContainer: n.probeContainer || null,
      origS3Url: n.origS3Url || null,
      normalizedAt: n.normalizedAt || null,
      updatedAt: n.updatedAt || null
    } : null,
    // Server's supersede-on-renormalize projection (Phase 3.5 Option A):
    // when the last clone attempt was `failed`/`elabs_failed` and a
    // successful renormalize has landed since, the row's `clone` is null
    // (so the UI shows "Not Seeded") and this block carries the forensic
    // detail of the prior failure. Null for freshly-seeded rows.
    lastFailedAttempt: row.lastFailedAttempt ? {
      cloneUuid: row.lastFailedAttempt.cloneUuid || null,
      cloneStatus: row.lastFailedAttempt.cloneStatus || null,
      attemptNumber: row.lastFailedAttempt.attemptNumber || 0,
      errorMessage: row.lastFailedAttempt.errorMessage || null,
      updatedAt: row.lastFailedAttempt.updatedAt || null
    } : null
  };
};

// ------------------------------------------------------------------ thunks

export const fetchPipeline = createAsyncThunk(
  'voiceCloning/fetchPipeline',
  async (userId, { rejectWithValue }) => {
    try {
      const url = config.GET_VOICE_CLONE_PIPELINE.replace(':userId', userId);
      // eslint-disable-next-line no-console
      console.log(`[voiceCloningSlice.fetchPipeline] GET ${url}`);
      const res = await fetch(url, { method: 'GET', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      // eslint-disable-next-line no-console
      console.log(
        `[voiceCloningSlice.fetchPipeline] user=${userId} received ${payload?.rows?.length || 0} row(s) ` +
        `mock=${!!payload?.mock}`,
        payload?.rows?.[0] ? { firstRow: payload.rows[0] } : ''
      );
      return payload;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[voiceCloningSlice.fetchPipeline] user=${userId} failed:`, e);
      return rejectWithValue(e.message);
    }
  }
);


export const reconcileClone = createAsyncThunk(
  'voiceCloning/reconcileClone',
  async (cloneUuid, { rejectWithValue, dispatch, getState }) => {
    try {
      const url = config.RECONCILE_VOICE_CLONE.replace(':cloneUuid', cloneUuid);
      const res = await fetch(url, { method: 'POST', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      // Reconcile only enqueues a job — refresh the pipeline shortly after.
      const userId = getState().user?.profile?.data?.userId;
      if (userId) dispatch(fetchPipeline(userId));
      return payload;
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const retryClone = createAsyncThunk(
  'voiceCloning/retryClone',
  async (cloneUuid, { rejectWithValue, dispatch, getState }) => {
    try {
      const url = config.RETRY_VOICE_CLONE.replace(':cloneUuid', cloneUuid);
      const res = await fetch(url, { method: 'POST', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      const userId = getState().user?.profile?.data?.userId;
      if (userId) dispatch(fetchPipeline(userId));
      return payload;
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const smokeTestClone = createAsyncThunk(
  'voiceCloning/smokeTestClone',
  async ({ cloneUuid, text }, { rejectWithValue, dispatch, getState }) => {
    try {
      const url = config.SMOKE_TEST_VOICE_CLONE.replace(':cloneUuid', cloneUuid);
      const res = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ text })
      });
      const payload = await handle(res, rejectWithValue);
      // Smoke-test is async on the server; refresh after a short delay so
      // the user sees the status transition + preview URL.
      const userId = getState().user?.profile?.data?.userId;
      if (userId) setTimeout(() => dispatch(fetchPipeline(userId)), 1500);
      return payload;
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const seedClone = createAsyncThunk(
  'voiceCloning/seedClone',
  async (speechSampleId, { rejectWithValue, dispatch, getState }) => {
    try {
      const res = await fetch(config.SEED_VOICE_CLONE, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ speechSampleId })
      });
      const payload = await handle(res, rejectWithValue);
      // Carry the sampleId back into the reducer so it can key the
      // "deferred" banner to the row that prompted it (the reducer can't
      // see `action.meta.arg` for the fulfilled payload otherwise without
      // re-plumbing every call site).
      const userId = getState().user?.profile?.data?.userId;
      if (userId) dispatch(fetchPipeline(userId));
      return { ...payload, speechSampleId };
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

/**
 * Re-enqueue a SampleNormalizer job for a specific VoiceSnippet. Backed by
 * POST /api/voice-snippets/:vsUuid/normalize/retry. Works for both failed
 * rows (transient fix) and legacy null-status rows (initial seed).
 */
export const retryNormalize = createAsyncThunk(
  'voiceCloning/retryNormalize',
  async (voiceSnippetUuid, { rejectWithValue, dispatch, getState }) => {
    if (!voiceSnippetUuid) {
      return rejectWithValue({ message: 'voiceSnippetUuid required' });
    }
    try {
      const url = config.RETRY_VOICE_SNIPPET_NORMALIZE.replace(':vsUuid', voiceSnippetUuid);
      // eslint-disable-next-line no-console
      console.log(`[voiceCloningSlice.retryNormalize] POST ${url}`);
      const res = await fetch(url, { method: 'POST', headers: authHeaders() });
      const payload = await handle(res, rejectWithValue);
      // Worker is async — refresh shortly so the UI picks up
      // normalizeStatus=processing, then ready (or failed again).
      const userId = getState().user?.profile?.data?.userId;
      if (userId) setTimeout(() => dispatch(fetchPipeline(userId)), 1200);
      return payload;
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const deleteClone = createAsyncThunk(
  'voiceCloning/deleteClone',
  async (cloneUuid, { rejectWithValue, dispatch, getState }) => {
    try {
      const url = config.DELETE_VOICE_CLONE.replace(':cloneUuid', cloneUuid);
      const res = await fetch(url, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        return rejectWithValue(err);
      }
      const userId = getState().user?.profile?.data?.userId;
      if (userId) dispatch(fetchPipeline(userId));
      return { cloneUuid };
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

// ------------------------------------------------------------------ slice

const initialState = {
  byId: {},                // { rowUuid: row } — keyed by clone uuid or sampleId
  order: [],               // ordered array of rowUuids from last fetch
  status: 'idle',          // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  mock: false,             // true when server is running in ELEVENLABS_MOCK mode
  actionStatus: {},        // { rowUuid: 'reconciling' | 'retrying' | 'smokeTesting' | 'seeding' | 'deleting' }
  lastSmokeTest: null,     // { cloneUuid, previewUrl, text }
  // Set when a Seed request returned `{ deferred: true }` from the server
  // (typically because `normalizeStatus !== 'ready'`). The UI shows a
  // dismissible banner at the top of the pipeline page so the BC isn't
  // left wondering why nothing happened. Cleared by `clearDeferredSeed()`
  // and automatically whenever another seed is attempted.
  deferredSeed: null,      // { speechSampleId, reason, normalizeStatus }
  filter: {
    status: 'all',
    bookId: null,
    litGroupId: null,
    characterId: null
  }
};

const slice = createSlice({
  name: 'voiceCloning',
  initialState,
  reducers: {
    setFilter(state, action) {
      state.filter = { ...state.filter, ...action.payload };
    },
    clearFilter(state) {
      state.filter = initialState.filter;
    },
    clearLastSmokeTest(state) {
      state.lastSmokeTest = null;
    },
    clearDeferredSeed(state) {
      state.deferredSeed = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchPipeline --------------------------------------------------
      .addCase(fetchPipeline.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchPipeline.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;

        // Accept either { rows, mock, count } envelope or a bare array.
        const payload = action.payload || {};
        const rawRows = Array.isArray(payload)
          ? payload
          : payload.rows || payload.clones || [];
        state.mock = !!payload.mock;

        // ---- Row-identity preservation (Fix D) --------------------------
        // The server re-signs `smokeTestUrl` on every call (new signed-URL
        // expiry/signature each request) and the reconcile cron can bump
        // `updatedAt` without changing anything material. If we naively
        // rebuilt `byId` wholesale, every row object would get a fresh
        // reference every poll — which defeats `React.memo` on the row
        // component and churns the whole list each tick.
        //
        // Strategy: diff each incoming row against the prior row for the
        // same uuid using a small set of **material** fields. If none
        // changed, keep the old object reference. This makes `===`-based
        // memo comparators in the UI fire only when something the user
        // can actually see has moved.
        //
        // `smokeTestUrl` is intentionally NOT in the material list — the
        // URL rotates on every poll but the underlying audio doesn't. The
        // pipeline page caches the first non-null URL per cloneUuid in a
        // ref for modal playback (see VoiceCloningPipeline.jsx, Fix A).
        const materialKeys = [
          'cloneStatus',
          'elevenLabsVoiceId',
          'externalStatus',
          'errorMessage',
          'attemptNumber',
          'hasClone',
          'approvedAt',
          'characterName',
          'variationName',
          'bookTitle',
          'litGroupName'
        ];
        const normalizeMaterialKeys = [
          'status',
          'error',
          'attempts',
          'voiceSnippetUuid'
        ];
        const isRowMaterialEqual = (prev, next) => {
          if (!prev || !next) return false;
          for (const k of materialKeys) {
            if (prev[k] !== next[k]) return false;
          }
          const p = prev.normalize;
          const n = next.normalize;
          if (!p !== !n) return false;
          if (p && n) {
            for (const k of normalizeMaterialKeys) {
              if (p[k] !== n[k]) return false;
            }
          }
          // lastFailedAttempt is a small object; compare by cloneUuid which
          // is the only field that can toggle presence.
          const lp = prev.lastFailedAttempt?.cloneUuid || null;
          const ln = next.lastFailedAttempt?.cloneUuid || null;
          if (lp !== ln) return false;
          // smokeTestUrl: capture the null→non-null transition (so the
          // modal can pick up the first playable URL) but ignore pure
          // re-signing churn (non-null → different non-null).
          if (!prev.smokeTestUrl !== !next.smokeTestUrl) return false;
          return true;
        };

        const prevById = state.byId;
        const nextById = {};
        const nextOrder = [];
        rawRows.forEach((raw) => {
          const row = flattenPipelineRow(raw);
          if (!row) return;
          const prior = prevById[row.uuid];
          // Preserve the old reference when nothing material changed AND
          // the presigned URL is still the one the UI already saw. When
          // the URL rotates, we swap in a merged object that keeps every
          // other field at its prior identity (so downstream selectors
          // that only care about e.g. cloneStatus see a true `===` hit).
          if (prior && isRowMaterialEqual(prior, row)) {
            if (prior.smokeTestUrl === row.smokeTestUrl) {
              nextById[row.uuid] = prior;
            } else {
              // Material fields unchanged, only the signed URL rotated.
              // Keep the prior URL so `<audio src>` doesn't thrash — the
              // prior presigned URL is still valid for an hour.
              nextById[row.uuid] = prior;
            }
          } else {
            nextById[row.uuid] = row;
          }
          nextOrder.push(row.uuid);
        });
        state.byId = nextById;
        state.order = nextOrder;
      })

      .addCase(fetchPipeline.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to load voice-cloning pipeline';
      })

      // reconcile ------------------------------------------------------
      .addCase(reconcileClone.pending, (state, action) => {
        state.actionStatus[action.meta.arg] = 'reconciling';
      })
      .addCase(reconcileClone.fulfilled, (state, action) => {
        delete state.actionStatus[action.meta.arg];
      })
      .addCase(reconcileClone.rejected, (state, action) => {
        delete state.actionStatus[action.meta.arg];
        state.error = action.payload || 'Reconcile failed';
      })

      // retry ----------------------------------------------------------
      .addCase(retryClone.pending, (state, action) => {
        state.actionStatus[action.meta.arg] = 'retrying';
      })
      .addCase(retryClone.fulfilled, (state, action) => {
        delete state.actionStatus[action.meta.arg];
      })
      .addCase(retryClone.rejected, (state, action) => {
        delete state.actionStatus[action.meta.arg];
        state.error = action.payload || 'Retry failed';
      })

      // smoke test -----------------------------------------------------
      .addCase(smokeTestClone.pending, (state, action) => {
        state.actionStatus[action.meta.arg.cloneUuid] = 'smokeTesting';
      })
      .addCase(smokeTestClone.fulfilled, (state, action) => {
        const cloneUuid = action.meta.arg.cloneUuid;
        delete state.actionStatus[cloneUuid];
        // Backend returns { queued: true } — preview URL will arrive on
        // the next fetchPipeline. Store the text the user asked for so the
        // modal can redisplay it.
        state.lastSmokeTest = {
          cloneUuid,
          text: action.meta.arg.text,
          previewUrl: null
        };
      })
      .addCase(smokeTestClone.rejected, (state, action) => {
        delete state.actionStatus[action.meta.arg.cloneUuid];
        state.error = action.payload || 'Smoke test failed';
      })

      // seed -----------------------------------------------------------
      .addCase(seedClone.pending, (state, action) => {
        // `action.meta.arg` is a speechSampleId — that's the row uuid for
        // a not-yet-seeded row.
        state.actionStatus[action.meta.arg] = 'seeding';
        // Any new seed attempt supersedes a prior deferred banner.
        state.deferredSeed = null;
      })
      .addCase(seedClone.fulfilled, (state, action) => {
        delete state.actionStatus[action.meta.arg];
        // Server gates seeds on `normalizeStatus === 'ready'` and returns
        // `{ deferred: true, reason, normalizeStatus }` when the gate
        // refuses. Surface that as a page-level banner; in the happy path
        // `deferred` is absent and we leave the banner null.
        const payload = action.payload || {};
        if (payload.deferred) {
          state.deferredSeed = {
            speechSampleId: payload.speechSampleId || action.meta.arg || null,
            reason: payload.reason || 'deferred',
            normalizeStatus: payload.normalizeStatus || null
          };
        }
      })
      .addCase(seedClone.rejected, (state, action) => {
        delete state.actionStatus[action.meta.arg];
        state.error = action.payload || 'Seed failed';
      })

      // delete ---------------------------------------------------------
      .addCase(deleteClone.pending, (state, action) => {
        state.actionStatus[action.meta.arg] = 'deleting';
      })
      .addCase(deleteClone.fulfilled, (state, action) => {
        delete state.actionStatus[action.meta.arg];
      })
      .addCase(deleteClone.rejected, (state, action) => {
        delete state.actionStatus[action.meta.arg];
        state.error = action.payload || 'Delete failed';
      })

      // retryNormalize -------------------------------------------------
      // Key actionStatus by VoiceSnippet UUID — the pipeline row's `normalize.voiceSnippetUuid`
      // — not by the row UUID. The UI reads this to spin the retry button while the
      // worker is being re-enqueued.
      .addCase(retryNormalize.pending, (state, action) => {
        state.actionStatus[action.meta.arg] = 'normalizing';
      })
      .addCase(retryNormalize.fulfilled, (state, action) => {
        delete state.actionStatus[action.meta.arg];
      })
      .addCase(retryNormalize.rejected, (state, action) => {
        delete state.actionStatus[action.meta.arg];
        state.error = action.payload || 'Normalize retry failed';
      });
  }
});

export const {
  setFilter,
  clearFilter,
  clearLastSmokeTest,
  clearDeferredSeed
} = slice.actions;

// ------------------------------------------------------------------ selectors

const root = (state) => state.voiceCloning || initialState;

export const selectCloneRows = (state) =>
  root(state).order.map((id) => root(state).byId[id]).filter(Boolean);

export const selectCloneById = (rowUuid) => (state) =>
  root(state).byId[rowUuid] || null;

/**
 * Look up a pipeline row by its underlying CharSpeechSample UUID.
 * Useful for inline badges on pages (e.g. VariationReviewRow) where the
 * caller only knows the sampleId, not the clone uuid.
 */
export const selectCloneBySampleId = (sampleId) => (state) => {
  if (!sampleId) return null;
  const rows = root(state).byId;
  // byId is keyed by cloneUuid when a clone exists, else sampleId.
  if (rows[sampleId]) return rows[sampleId];
  return (
    Object.values(rows).find((r) => r && r.sampleId === sampleId) || null
  );
};

export const selectCloneStatus = (state) => root(state).status;
export const selectCloneError = (state) => root(state).error;
export const selectMockMode = (state) => root(state).mock;
export const selectActionStatus = (rowUuid) => (state) =>
  root(state).actionStatus[rowUuid] || null;
export const selectLastSmokeTest = (state) => root(state).lastSmokeTest;
export const selectDeferredSeed = (state) => root(state).deferredSeed;
export const selectCloneFilter = (state) => root(state).filter;

/**
 * Returns count breakdown keyed by cloneStatus for the summary bar at
 * the top of the pipeline page.
 */
export const selectCloneStatusCounts = (state) => {
  const rows = selectCloneRows(state);
  return rows.reduce((acc, r) => {
    const k = r.cloneStatus || 'none';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
};

export default slice.reducer;
